import { SubscriptionPlan } from './subscriptions';

/**
 * A server plan, in the shape the subscription card already draws.
 *
 * ---------------------------------------------------------------------------
 * WHY AN ADAPTER RATHER THAN CHANGING THE SCREEN'S SHAPE
 * ---------------------------------------------------------------------------
 * `data/subscription.ts` modelled exactly two plans keyed `monthly | yearly`,
 * with presentational fields the backend has no notion of - a struck-through
 * `was` price, a `saving` percentage, a `billed` sentence, a bullet list. The
 * server instead returns an arbitrary number of plans identified by UUID, each
 * carrying the perks it actually grants.
 *
 * Rewriting the card to consume raw plans would mean rebuilding a 689-line
 * screen. Mapping instead keeps the design intact and puts every derivation in
 * one readable place - which matters, because some of these fields CANNOT be
 * derived honestly and this file is where that is admitted.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DERIVED, AND WHAT IS DROPPED
 * ---------------------------------------------------------------------------
 *   label / title    the plan's own name - no invention
 *   price            `priceAmount`, a decimal string, parsed once here
 *   billed           from `durationDays`, phrased in the term the plan uses
 *   points           built from the perks the plan REALLY grants
 *   recommended      the longest-duration plan, as the nearest honest proxy
 *
 *   was / saving     DROPPED unless a longer plan exists to compare against.
 *                    The mock hard-coded a list price to strike through. There
 *                    is no such field on the server, and inventing one would
 *                    put a fake discount on a real price - the one thing a
 *                    pricing screen must never do.
 */
export type PlanView = {
  /** The plan's real id. Pass this to `purchase()`. */
  key: string;
  label: string;
  title: string;
  /** Whole rupees. */
  price: number;
  /** Only set when a genuine comparison exists. Never invented. */
  was?: number;
  billed: string;
  /** Percent saved against the shortest plan, pro-rated. Zero when unknown. */
  saving: number;
  savingRupees: number;
  recommended: boolean;
  points: string[];
};

/** "billed monthly" / "billed yearly" / "billed every 90 days". */
function billedPhrase(durationDays: number): string {
  if (durationDays >= 28 && durationDays <= 31) return 'billed monthly';
  if (durationDays >= 88 && durationDays <= 92) return 'billed quarterly';
  if (durationDays >= 360 && durationDays <= 370) return 'billed yearly';
  return `billed every ${durationDays} days`;
}

/** A short term label for the tab - "Monthly", "Yearly", "90 days". */
function termLabel(durationDays: number): string {
  if (durationDays >= 28 && durationDays <= 31) return 'Monthly';
  if (durationDays >= 88 && durationDays <= 92) return 'Quarterly';
  if (durationDays >= 360 && durationDays <= 370) return 'Yearly';
  return `${durationDays} days`;
}

/**
 * The ticks inside the card, from perks the plan actually grants.
 *
 * Every line is conditional: a plan without priority dispatch does not get a
 * "priority dispatch" tick. The mock listed the same bullets on both plans
 * regardless of what either did.
 */
function pointsFor(plan: SubscriptionPlan): string[] {
  const points: string[] = [];

  const discount = Number(plan.discountPercent);
  if (discount > 0) {
    const cap = plan.maxDiscountAmount
      ? ` (up to ₹${plan.maxDiscountAmount} a booking)`
      : '';
    points.push(`${discount}% off every booking${cap}`);
  }

  const multiplier = Number(plan.coinEarnMultiplier);
  if (multiplier > 1) {
    points.push(`${multiplier}× Homingo Coins on every job`);
  }

  if (plan.bonusCoins > 0) {
    points.push(`${plan.bonusCoins} bonus coins when you join`);
  }
  if (plan.waivesCancellationFee) {
    points.push('No cancellation fee');
  }
  if (plan.extraReschedules > 0) {
    points.push(
      `${plan.extraReschedules} extra reschedule${plan.extraReschedules === 1 ? '' : 's'}`,
    );
  }
  if (plan.priorityDispatch) {
    points.push('Priority booking');
  }
  if (plan.includedBookings !== null) {
    points.push(`Covers ${plan.includedBookings} bookings`);
  }

  return points;
}

/**
 * Map the server's plans into cards.
 *
 * `was` and `saving` are only populated where a real comparison exists: the
 * cheapest per-day plan is the baseline, and a longer plan is compared against
 * what that baseline would have cost over the same term. When there is only
 * one plan there is nothing to compare and both stay empty.
 */
export function toPlanViews(plans: SubscriptionPlan[]): PlanView[] {
  if (plans.length === 0) return [];

  const sorted = [...plans].sort((a, b) => a.durationDays - b.durationDays);
  const baseline = sorted[0];
  const baselinePerDay = Number(baseline.priceAmount) / baseline.durationDays;
  const longest = sorted[sorted.length - 1];

  return sorted.map(plan => {
    const price = Number(plan.priceAmount);
    const comparable = plan.id !== baseline.id && baselinePerDay > 0;
    const wouldHaveCost = comparable
      ? Math.round(baselinePerDay * plan.durationDays)
      : 0;
    const savingRupees =
      comparable && wouldHaveCost > price ? wouldHaveCost - price : 0;

    return {
      key: plan.id,
      label: termLabel(plan.durationDays),
      title: plan.name,
      price,
      was: savingRupees > 0 ? wouldHaveCost : undefined,
      billed: billedPhrase(plan.durationDays),
      saving:
        savingRupees > 0 ? Math.round((savingRupees / wouldHaveCost) * 100) : 0,
      savingRupees,
      // The longest term, as the nearest honest proxy for "recommended" -
      // the server has no such flag and one must not be fabricated per-plan.
      recommended: plan.id === longest.id && sorted.length > 1,
      points: pointsFor(plan),
    };
  });
}
